import { Button, Input, Label } from "@/components/ui"
import { TooltipInfo } from "@/components/ui/TooltipInfo"
import { useEffect, useState } from "react"
import { TimeSelect } from "../components/TimeSelect"
import { Loader2, PlusIcon } from "lucide-react"
import { useEmailTypeSettings, useUpdateEmailTypeSettings } from "@/hooks/useEmailSettings"
import { Example } from "./ExampleContent"
import { Switch } from "@/components/ui/switch"

type TimeUnit = "minutes" | "hours" | "days"
type Timing = { value: number; unit: TimeUnit }
type OriginalValues = { subjects: string; prompt: string; timings: Timing[]; enabled: boolean }
export const RemindersEmail = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("")
  const [prompt, setPrompt] = useState<string>("")
  const [timings, setTimings] = useState<Timing[]>([
    {
      value: 1,
      unit: "minutes"
    }
  ])
  const [originalValues, setOriginalValues] = useState<OriginalValues>({ subjects: '', prompt: '', timings: [], enabled: false });
  const handleAddTiming = () => setTimings([...timings, {value: 1, unit: "minutes"}]);
  const handleRemoveTiming = (index: number) => setTimings(timings.filter((_, i) => i !== index));
  const handleTimingChange = (index: number, timing: Timing) => setTimings(timings.map((t, i) => i === index ? timing : t));
  const { data: email } = useEmailTypeSettings('reminder');
  const { mutate: updateEmailSettings, isPending } = useUpdateEmailTypeSettings();

  const cancel = () => {
    setSubject(originalValues.subjects);
    setPrompt(originalValues.prompt);
    setTimings(originalValues.timings);
    setEnabled(originalValues.enabled);
  }

  const handleToggleEnabled = (newStatus: boolean) => {
    updateEmailSettings(
      { 
        type: 'reminder', 
        settings: { 
          subject: email?.reminder?.subject || '',
          prompt: email?.reminder?.prompt || '',
          timings: email?.reminder?.timings || [{value: 1, unit: "minutes"}],
          enabled: newStatus 
        } 
      },
      { onSuccess: () => setEnabled(newStatus) }
    );
  }
  useEffect(() => {
    setSubject(email?.reminder?.subject || '');
    setPrompt(email?.reminder?.prompt || '');
    setTimings(email?.reminder?.timings || [{value: 1, unit: "minutes"}]);
    if (email?.reminder?.enabled !== undefined)  setEnabled(email.reminder.enabled); 
    setOriginalValues({ subjects: email?.reminder?.subject || '', prompt: email?.reminder?.prompt || '', timings: email?.reminder?.timings || [{value: 1, unit: "minutes"}], enabled: email?.reminder?.enabled || false });
  }, [email?.reminder?.subject, email?.reminder?.prompt, email?.reminder?.timings]);

  const subjectClean = subject.trim();
  const promptClean = prompt.trim();
  const subjectChanged = subjectClean !== (originalValues.subjects?.trim() ?? '') && subjectClean !== '';
  const promptChanged = promptClean !== (originalValues.prompt?.trim() ?? '') && promptClean !== '';
  const timingsChanged = (() => {
    const a = timings;
    const b = (originalValues.timings || []) as Timing[];
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.value !== b[i]?.value || a[i]?.unit !== b[i]?.unit) return true;
    }
    return false;
  })();
  const isChanged = subjectChanged || promptChanged || timingsChanged;

  return (
    <div className="col-span-3 h-full relative">  
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
            Email Reminders Settings
            {!enabled && <div className={`px-2  flex items-center font-medium justify-center text-xs text-white rounded-full bg-gray-300`}>
               Disabled
            </div>}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-1">Clients will automatically receive a reminder email before their appointment.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggleEnabled} disabled={isPending} />
      </div>

      {!enabled && <div className="absolute top-20 right-0 left-0 bottom-0 bg-white/80 z-10"/>}
      <div className="flex flex-col gap-10 mt-10 ">
        <div>
          <div className="flex items-center mb-1 gap-2">
            <Label htmlFor="subject" className="font-semibold text-gray-900 text-base">Subject</Label>
            <TooltipInfo description="Set your own subject of the reminder email." />
          </div>
          <Input id="subject" placeholder="Subject" className="mt-1 h-12 block w-full" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>


        <div className="flex bg-gray-50 flex-col w-full p-4 rounded-lg">
          <div className="flex items-center mb-1 gap-2">
            <p  className="font-semibold text-gray-900 text-base">Timing</p>
            <TooltipInfo description="Timing of the reminder email will be sent for clients before event." />
          </div>
          <div className="flex flex-col gap-2"> 
          {timings.map((timing, index) => (
            <TimeSelect
              isFirst={index === 0}
              key={index}
              value={timing}
              onChange={(value) => handleTimingChange(index, value)}
              onRemove={() => handleRemoveTiming(index)}
            />
          ))}
          {timings.length < 3 && <button 
              className="font-light  text-gray-500 opacity-50 hover:opacity-100 dark:text-gray-400  mt-2 flex items-center gap-2"
              onClick={handleAddTiming}> <PlusIcon className="size-4"
            /> Add another timing</button> }
          </div>
        </div>

        <Example type="reminder" />

      </div>
      {isChanged && 
        <div className="flex gap-2 mt-20 ">
          <Button 
            variant="default" 
            className="w-[100px]" 
            onClick={()=> updateEmailSettings({ type: 'reminder', settings: { subject, prompt, timings, enabled } })} 
            disabled={isPending}
          >
            {isPending 
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Save</>) 
              : ('Save')
            }
          </Button>
          <Button variant="outline" className="px-6" onClick={cancel}>
            Cancel
          </Button>
        </div>
      }


    </div>
  )
}